import ApplicationController from './application_controller'

export default class extends ApplicationController {
  static targets = [
    'panel',
    'iframe',
    'launcher',
    'backdrop'
  ]

  static values = {
    newConversationUrl: String,
    inloopId: String,
    engagementId: String,
    brainDigitalGardenId: String,
    llmsDigitalGardenId: String,
    contextMode: String
  }

  toggle () {
    if (this.panelTarget.classList.contains('hidden')) {
      this.open()
    } else {
      this.close()
    }
  }

  open () {
    this.panelTarget.classList.remove('hidden')
    this.backdropTarget.classList.remove('hidden')

    if (window.matchMedia('(min-width: 768px)').matches) {
      document.body.classList.add('chat-overlay-desktop-open')
    } else {
      document.body.classList.add('overflow-hidden')
    }

    // Immediately create a new normal conversation and load kiosk view
    this.createNormalAndLoad()
  }

  close () {
    this.panelTarget.classList.add('hidden')
    this.backdropTarget.classList.add('hidden')
    document.body.classList.remove('overflow-hidden')
    document.body.classList.remove('chat-overlay-desktop-open')
    // Clear iframe to avoid lingering websocket connections
    this.iframeTarget.src = 'about:blank'
  }

  async reset () {
    try {
      const src = this.iframeTarget?.src
      if (!src || src === 'about:blank') {
        // No active conversation loaded; just create a new one
        await this.createNormalAndLoad()
        return
      }

      const tokenEl = document.querySelector('meta[name="csrf-token"]')
      const csrf = tokenEl ? tokenEl.getAttribute('content') : undefined
      const urlObj = new URL(src, window.location.origin)
      const basePath = urlObj.pathname // e.g. /conversations/:id
      const resetUrl = new URL(`${basePath}/reset_shared_context`, window.location.origin)

      const res = await fetch(resetUrl.toString(), {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {})
        },
        credentials: 'same-origin'
      })

      if (!res.ok) throw new Error('Failed to reset chat')

      // Now create a new conversation bound to the same shared_context_root_id
      await this.createNormalAndLoad()
    } catch (e) {
      document.dispatchEvent(new CustomEvent('conversationError', {
        detail: {
          errorType: 'reset_conversation_failed',
          message: e.message || 'Unable to reset chat right now.',
          showContactSupport: false
        }
      }))
    }
  }

  async createNormalAndLoad () {
    try {
      if (!this.hasNewConversationUrlValue || !this.newConversationUrlValue) {
        throw new Error('No default bot available for chat')
      }

      const tokenEl = document.querySelector('meta[name="csrf-token"]')
      const csrf = tokenEl ? tokenEl.getAttribute('content') : undefined

      const res = await fetch(this.newConversationUrlValue, {
        method: 'POST',
        headers: {
          ...(csrf ? { 'X-CSRF-Token': csrf } : {})
        },
        redirect: 'follow',
        credentials: 'same-origin'
      })

      // After redirects, res.url is the conversation URL
      const convoUrl = res.url
      if (!convoUrl || !convoUrl.match(/\/conversations\//)) {
        throw new Error('Failed to create conversation')
      }

      await this.addContexts(convoUrl, csrf)

      const kioskUrl = this.withKioskParam(convoUrl)
      this.iframeTarget.src = kioskUrl
    } catch (e) {
      // Surface an application-wide conversation error
      document.dispatchEvent(new CustomEvent('conversationError', {
        detail: {
          errorType: 'create_conversation_failed',
          message: e.message || 'Unable to start a new chat right now.',
          showContactSupport: false
        }
      }))
      this.close()
    }
  }

  async addContexts (convoUrl, csrf) {
    const urlObj = new URL(convoUrl, window.location.origin)
    const basePath = urlObj.pathname
    const headers = {
      'Content-Type': 'application/json',
      ...(csrf ? { 'X-CSRF-Token': csrf } : {})
    }

    const requests = []

    if (this.hasInloopIdValue && this.inloopIdValue) {
      requests.push(this.postContext(`${basePath}/add_inloop_context`, headers, { inloop_id: this.inloopIdValue }))
    }

    if (this.hasEngagementIdValue && this.engagementIdValue) {
      requests.push(this.postContext(`${basePath}/add_engagement_context`, headers, { engagement_id: this.engagementIdValue }))
    }

    const digitalGardenIds = new Set()
    if (this.hasBrainDigitalGardenIdValue && this.brainDigitalGardenIdValue) digitalGardenIds.add(this.brainDigitalGardenIdValue)
    if (this.hasLlmsDigitalGardenIdValue && this.llmsDigitalGardenIdValue) digitalGardenIds.add(this.llmsDigitalGardenIdValue)

    digitalGardenIds.forEach((id) => {
      requests.push(this.postContext(`${basePath}/add_digital_garden_context`, headers, { digital_garden_id: id }))
    })

    if (requests.length === 0) return

    try {
      await Promise.allSettled(requests)
    } catch (error) {
      console.warn('Failed to attach contexts to facilitator conversation', error)
    }
  }

  async postContext (path, headers, payload) {
    const url = new URL(path, window.location.origin)
    try {
      await fetch(url.toString(), {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        credentials: 'same-origin'
      })
    } catch (error) {
      console.warn('Context attachment request failed', path, error)
      throw error
    }
  }

  withKioskParam (url) {
    try {
      const u = new URL(url, window.location.origin)
      u.searchParams.set('kiosk', '1')
      if (this.hasInloopIdValue && this.inloopIdValue) u.searchParams.set('ctx_inloop_id', this.inloopIdValue)
      if (this.hasEngagementIdValue && this.engagementIdValue) u.searchParams.set('ctx_engagement_id', this.engagementIdValue)
      if (this.hasBrainDigitalGardenIdValue && this.brainDigitalGardenIdValue) u.searchParams.set('ctx_brain_dg_id', this.brainDigitalGardenIdValue)
      if (this.hasLlmsDigitalGardenIdValue && this.llmsDigitalGardenIdValue) u.searchParams.set('ctx_llms_dg_id', this.llmsDigitalGardenIdValue)
      if (this.hasContextModeValue && this.contextModeValue) u.searchParams.set('ctx_mode', this.contextModeValue)
      return u.toString()
    } catch {
      // Fallback: append naïvely
      const extra = []
      if (this.hasInloopIdValue && this.inloopIdValue) extra.push(`ctx_inloop_id=${this.inloopIdValue}`)
      if (this.hasEngagementIdValue && this.engagementIdValue) extra.push(`ctx_engagement_id=${this.engagementIdValue}`)
      if (this.hasBrainDigitalGardenIdValue && this.brainDigitalGardenIdValue) extra.push(`ctx_brain_dg_id=${this.brainDigitalGardenIdValue}`)
      if (this.hasLlmsDigitalGardenIdValue && this.llmsDigitalGardenIdValue) extra.push(`ctx_llms_dg_id=${this.llmsDigitalGardenIdValue}`)
      if (this.hasContextModeValue && this.contextModeValue) extra.push(`ctx_mode=${this.contextModeValue}`)
      const base = url + (url.includes('?') ? '&' : '?') + 'kiosk=1'
      return extra.length ? base + '&' + extra.join('&') : base
    }
  }
}
